import React, { useCallback, useMemo, ChangeEvent, KeyboardEvent } from 'react'
import { useForm } from 'react-hook-form'
import { useSelector, useDispatch } from 'react-redux'
import { AppState } from '../store'
import { FormInput } from './FinancialCalculator'
import { fixedNumber } from '../js/utils'
import {
  changeFetchingRestype,
  RESTYPE_ADDITION,
  RESTYPE_INTEREST,
  RESTYPE_MONTHS,
  RESTYPE_TARGET,
  fetchFinancialCalc,
} from '../store/modules/financialCalc'
import { FinancialCalcReq } from '../api/selfApi'
import '../assets/css/libs.css'
import '../assets/css/style.css'

interface CalcFormProps {
  children: React.ReactNode
  caseType: 'target' | 'interest' | 'months' | 'addition'
  setInputErrorMessage: (msg: string) => void
  isResponsive: boolean
}

function getDecimalPointLength(value: string) {
  const numberParts = value.split('.')
  return numberParts[1] ? numberParts[1].length : 0
}

export const defaultValues = {
  initial: 100,
  addition: 3.0,
  interest: 1.0,
  years: 20,
  target: 1000,
  depositInterest: 0.1,
}

export const constraintValues = {
  initial: {
    upper: 99999.9,
    lower: 0,
    decimalPoint: 1,
  },
  addition: {
    upper: 999.9,
    lower: 0,
    decimalPoint: 1,
  },
  interest: {
    upper: 99.99,
    lower: 0,
    decimalPoint: 2,
  },
  target: {
    upper: 999999,
    lower: 0,
    decimalPoint: 0,
  },
  years: {
    upper: 99,
    lower: 1,
    decimalPoint: 0,
  },
  depositInterest: {
    upper: 99.99,
    lower: 0,
    decimalPoint: 2,
  },
}

function makeRequest(
  arg: FormInput,
  tab: 'target' | 'interest' | 'months' | 'addition'
): FinancialCalcReq {
  switch (tab) {
    case RESTYPE_TARGET:
      return {
        target: 0,
        initial: arg.initial * 10000,
        addition: arg.addition * 10000,
        interest: Number(arg.interest),
        depositInterest: Number(arg.depositInterest),
        months: arg.years * 12,
        resType: 'target',
      } as FinancialCalcReq
    case RESTYPE_MONTHS:
      return {
        target: arg.target * 10000,
        initial: arg.initial * 10000,
        addition: arg.addition * 10000,
        interest: Number(arg.interest),
        depositInterest: Number(arg.depositInterest),
        months: 0,
        resType: 'months',
      } as FinancialCalcReq
    case RESTYPE_INTEREST:
      return {
        target: arg.target * 10000,
        initial: arg.initial * 10000,
        addition: arg.addition * 10000,
        interest: 0,
        depositInterest: 0,
        months: arg.years * 12,
        resType: 'interest',
      } as FinancialCalcReq
    case RESTYPE_ADDITION:
      return {
        target: arg.target * 10000,
        initial: arg.initial * 10000,
        addition: 0,
        interest: Number(arg.interest),
        depositInterest: Number(arg.depositInterest),
        months: arg.years * 12,
        resType: 'addition',
      } as FinancialCalcReq
  }
}

export const CalcForm: React.FC<CalcFormProps> = React.memo((props) => {
  const dispatch = useDispatch()
  const { fetchState } = useSelector((state: AppState) => state.financialCalc)
  const { children, caseType, setInputErrorMessage } = props
  const { register, handleSubmit, setValue, getValues } = useForm<FormInput>()

  const onSubmit = useCallback(
    (arg: FormInput, caseType: 'target' | 'interest' | 'months' | 'addition') => {
      const _fetchingRestype = dispatch(changeFetchingRestype(caseType)).payload
      const param = makeRequest(arg, _fetchingRestype)
      if (
        param.resType !== RESTYPE_TARGET &&
        (param.target <= param.initial || param.target <= param.addition)
      ) {
        setInputErrorMessage('入力金額が目標を達成しています。')
        return
      }
      if (param.resType !== RESTYPE_ADDITION && param.initial === 0 && param.addition === 0) {
        setInputErrorMessage(
          '入力された値では計算結果が0となります。初期投資、積立額を設定して下さい。'
        )
        return
      }
      if (param !== null) {
        dispatch(fetchFinancialCalc(param))
      }
    },
    [dispatch, setInputErrorMessage]
  )

  const handleUpDownKey = useCallback(
    (
      e: KeyboardEvent<HTMLInputElement>,
      inputName: 'initial' | 'addition' | 'interest' | 'target' | 'depositInterest' | 'years',
      step: number,
      decimalPoint: number
    ) => {
      const value = getValues(inputName)
      const nowValue = value !== undefined ? Number(value) : Number(defaultValues[inputName])

      if (e.key === 'ArrowUp') {
        const calcValue = nowValue + step
        const newValue =
          calcValue <= constraintValues[inputName].upper
            ? Number(fixedNumber(calcValue, decimalPoint))
            : constraintValues[inputName].upper
        setValue(inputName, newValue, { shouldValidate: true })
        e.preventDefault()
        return
      } else if (e.key === 'ArrowDown') {
        const calcValue = nowValue - step
        const newValue =
          calcValue >= constraintValues[inputName].lower
            ? Number(fixedNumber(calcValue, decimalPoint))
            : constraintValues[inputName].lower
        setValue(inputName, newValue, { shouldValidate: true })
        e.preventDefault()
        return
      }
    },
    [getValues, setValue]
  )

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>, upper: number, lower: number, decimalPoint: number) => {
      const inputValue = e.target.value
      const inputName = e.target.name as
        | 'initial'
        | 'addition'
        | 'interest'
        | 'target'
        | 'depositInterest'
        | 'years'
      if (inputValue === '') {
        setValue(inputName, defaultValues[inputName], {
          shouldValidate: true,
        })
        return
      }
      if (inputValue.match(/^[0-9]+\.?[0-9]*$/) === null || isNaN(parseFloat(inputValue))) {
        setValue(inputName, defaultValues[inputName], {
          shouldValidate: true,
        })
        return
      }
      if (Number(inputValue) > upper) {
        setValue(inputName, defaultValues[inputName], {
          shouldValidate: true,
        })
        return
      }
      if (Number(inputValue) < lower) {
        setValue(inputName, defaultValues[inputName], {
          shouldValidate: true,
        })
        return
      }
      if (getDecimalPointLength(inputValue) > decimalPoint) {
        if (decimalPoint === 0) {
          setInputErrorMessage('小数点第1位を四捨五入して表示します。')
          setValue(inputName, Math.round(Number(inputValue)), {
            shouldValidate: true,
          })
          return
        } else {
          if (decimalPoint === 1) {
            setInputErrorMessage('小数点第2位を四捨五入して表示します。')
          } else {
            setInputErrorMessage('小数点第3位を四捨五入して表示します。')
          }
          setValue(inputName, Number(fixedNumber(inputValue, decimalPoint)), {
            shouldValidate: true,
          })
          return
        }
      }
      if (inputValue.match(/^0/) !== null) {
        setValue(inputName, Number(inputValue), {
          shouldValidate: true,
        })
      }
    },
    [setValue, setInputErrorMessage]
  )

  const isPending = useMemo(() => fetchState === 'pending', [fetchState])

  const InputAddition = useCallback(() => {
    return (
      <div className="c-form-group">
        <span className="c-form-group__sup">積立額（毎月）</span>
        <span className="c-form-group__input">
          <span className="c-form-group__text u-mr1em u-size--md-w58px u-text--md-right">毎月</span>
          <input
            className="c-form__input u-text--right u-size--w105px u-size--md-w140px"
            type="number"
            name="addition"
            step="0.1"
            defaultValue={defaultValues.addition}
            onKeyDown={(e) =>
              handleUpDownKey(e, 'addition', 0.1, constraintValues.addition.decimalPoint)
            }
            onBlurCapture={(e) =>
              handleChange(
                e,
                constraintValues.addition.upper,
                constraintValues.addition.lower,
                constraintValues.addition.decimalPoint
              )
            }
            ref={register({
              required: true,
              min: constraintValues.addition.lower,
              max: constraintValues.addition.upper,
            })}
          />
          <span className="c-form-group__text u-size--md-w64px">万円を</span>
        </span>
      </div>
    )
  }, [handleChange, handleUpDownKey, register])

  const InputInterest = useCallback(() => {
    return (
      <div className="c-form-group">
        <span className="c-form-group__sup">利回り（年率）</span>
        <span className="c-form-group__input">
          <span className="c-form-group__text u-size--md-w64px"></span>
          <input
            className="c-form__input u-text--right u-size--w105px"
            type="number"
            name="interest"
            step="0.01"
            defaultValue={defaultValues.interest}
            onKeyDown={(e) =>
              handleUpDownKey(e, 'interest', 0.01, constraintValues.interest.decimalPoint)
            }
            onBlurCapture={(e) =>
              handleChange(
                e,
                constraintValues.interest.upper,
                constraintValues.interest.lower,
                constraintValues.interest.decimalPoint
              )
            }
            ref={register({
              required: true,
              min: constraintValues.interest.lower,
              max: constraintValues.interest.upper,
            })}
          />
          <span className="c-form-group__text u-size--md-w64px">%でつみたて投資</span>
        </span>
      </div>
    )
  }, [handleChange, handleUpDownKey, register])

  const InputMonths = useCallback(() => {
    return (
      <div className="c-form-group">
        <span className="c-form-group__sup">期間</span>
        <span className="c-form-group__input">
          <span className="c-form-group__text u-size--md-w74px"></span>
          <input
            className="c-form__input u-text--right u-size--w80px u-size--md-w140px"
            type="number"
            name="years"
            step="1"
            defaultValue={defaultValues.years}
            onKeyDown={(e) => handleUpDownKey(e, 'years', 1, constraintValues.years.decimalPoint)}
            onBlurCapture={(e) =>
              handleChange(
                e,
                constraintValues.years.upper,
                constraintValues.years.lower,
                constraintValues.years.decimalPoint
              )
            }
            ref={register({
              required: true,
              min: constraintValues.years.lower,
              max: constraintValues.years.upper,
            })}
          />
          <span className="c-form-group__text u-size--md-w64px">年間</span>
        </span>
      </div>
    )
  }, [handleChange, handleUpDownKey, register])

  const InputTarget = useCallback(() => {
    const str = caseType === 'interest' ? 'つみたて' : ''
    return (
      <div className="c-form-group">
        <span className="c-form-group__sup">目標額</span>
        <span className="c-form-group__input">
          <input
            // TODO デザイン class?
            className="c-form__input u-text--right u-size--w140px"
            type="number"
            name="target"
            step="1"
            defaultValue={defaultValues.target}
            onKeyDown={(e) => handleUpDownKey(e, 'target', 1, constraintValues.target.decimalPoint)}
            onBlurCapture={(e) =>
              handleChange(
                e,
                constraintValues.target.upper,
                constraintValues.target.lower,
                constraintValues.target.decimalPoint
              )
            }
            ref={register({
              required: true,
              min: constraintValues.target.lower,
              max: constraintValues.target.upper,
            })}
          />
          <span className="c-form-group__text">万円まで{str}</span>
        </span>
      </div>
    )
  }, [handleChange, handleUpDownKey, register, caseType])

  const Grid1Cols = useCallback(() => {
    return (
      <React.Fragment>
        {caseType === 'target' && (
          <React.Fragment>
            <div className="c-grid__col u-ml30px">
              <InputAddition />
            </div>
            <div className="c-grid__col u-ml30px">
              <InputMonths />
            </div>
          </React.Fragment>
        )}
        {caseType === 'interest' && (
          <React.Fragment>
            <div className="c-grid__col u-ml30px">
              <InputAddition />
            </div>
            <div className="c-grid__col u-ml30px">
              <InputMonths />
            </div>
            <div className="c-grid__col u-ml30px">
              <InputTarget />
            </div>
          </React.Fragment>
        )}
        {caseType === 'addition' && (
          <React.Fragment>
            <div className="c-grid__col u-ml30px">
              <InputMonths />
            </div>
            <div className="c-grid__col u-ml30px">
              <InputTarget />
            </div>
          </React.Fragment>
        )}
        {caseType === 'months' && (
          <React.Fragment>
            <div className="c-grid__col u-ml30px">
              <InputAddition />
            </div>
            <div className="c-grid__col u-ml30px">
              <InputTarget />
            </div>
          </React.Fragment>
        )}
      </React.Fragment>
    )
  }, [caseType])

  const leadStr = useMemo(() => {
    switch (caseType) {
      case 'target':
        return 'あなたの資産は将来いくらになる？'
      case 'interest':
        return 'あなたの目標額を達成するために必要な利回りは？'
      case 'addition':
        return 'あなたの目標額を達成するために必要な月々の積立額は？'
      case 'months':
        return 'あなたの目標額を達成するために必要な投資期間は？'
    }
  }, [caseType])

  const gtmId = useMemo(() => {
    switch (caseType) {
      case 'target':
        return 'btn_deposit'
      case 'interest':
        return 'btn_interest'
      case 'addition':
        return 'btn_fixedDeposit'
      case 'months':
        return 'btn_term'
    }
  }, [caseType])

  return (
    <React.Fragment>
      <p className="p-simulation__lead">{leadStr}</p>
      <form onSubmit={handleSubmit((arg) => onSubmit(arg, caseType))} autoComplete="off">
        <div className="c-grid--justify-content-center">
          <div className="c-grid__col">
            <div className="c-form-group">
              <span className="c-form-group__sup">初期投資</span>
              <span className="c-form-group__input">
                <span className="c-form-group__text u-size--md-w74px"></span>
                <input
                  className="c-form__input u-text--right u-size--w140px u-size--md-w140px"
                  type="number"
                  name="initial"
                  step="any"
                  defaultValue={defaultValues.initial}
                  onKeyDown={(e) =>
                    handleUpDownKey(e, 'initial', 10, constraintValues.initial.decimalPoint)
                  }
                  onBlurCapture={(e) =>
                    handleChange(
                      e,
                      constraintValues.initial.upper,
                      constraintValues.initial.lower,
                      constraintValues.initial.decimalPoint
                    )
                  }
                  ref={register({
                    required: true,
                    min: constraintValues.initial.lower,
                    max: constraintValues.initial.upper,
                  })}
                />
                <span className="c-form-group__text u-size--md-w64px">万円から</span>
              </span>
            </div>
          </div>
          <Grid1Cols />
        </div>
        {caseType !== 'interest' && (
          <div className="p-simulation__grid-2 c-grid--justify-content-center u-mt24px">
            <div className="p-simulation__grid-2__col">
              <InputInterest />
            </div>
            <div className="p-simulation__grid-2__col">
              <div className="c-form-group">
                <span className="c-form-group__sup">預金金利（年率）</span>
                <span className="c-form-group__input">
                  <input
                    className="c-form__input u-text--right u-size--w120px"
                    type="number"
                    name="depositInterest"
                    step="0.01"
                    defaultValue={defaultValues.depositInterest}
                    onKeyDown={(e) =>
                      handleUpDownKey(
                        e,
                        'depositInterest',
                        0.01,
                        constraintValues.depositInterest.decimalPoint
                      )
                    }
                    onBlurCapture={(e) =>
                      handleChange(
                        e,
                        constraintValues.depositInterest.upper,
                        constraintValues.depositInterest.lower,
                        constraintValues.depositInterest.decimalPoint
                      )
                    }
                    ref={register({
                      required: true,
                      min: constraintValues.depositInterest.lower,
                      max: constraintValues.depositInterest.upper,
                    })}
                  />
                  <span className="c-form-group__text">%で預金</span>
                </span>
              </div>
            </div>
          </div>
        )}
        <div className="u-text--center u-mt24px">
          <button className="c-button-calc" type="submit" disabled={isPending} id={gtmId}>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 164 92">
              <defs>
                <filter
                  id={`${caseType}_submit`}
                  x="0"
                  y="0"
                  viewBox="0 0 164 92"
                  filterUnits="userSpaceOnUse"
                >
                  <feOffset dx="4" dy="4" />
                  <feGaussianBlur result="blur" />
                  <feFlood floodColor="#5a435a" />
                  <feComposite operator="in" in2="blur" />
                  <feComposite in="SourceGraphic" />
                </filter>
              </defs>
              <g filter={`url(#${caseType}_submit)`}>
                <g fill="#fff" className="c-button-calc__bg">
                  <path d="M80 85.854L1.998 55.63V10.001c0-4.411 3.59-8 8.002-8h140.002c4.412 0 8 3.589 8 8V55.63L80 85.854z" />
                  <path
                    d="M80 83.709l76.002-29.449V10.001c0-3.308-2.691-6-6-6H10a6.008 6.008 0 00-6.002 6V54.26L80 83.71m0 4.29L-.002 57V10.001c0-5.524 4.477-10 10.002-10h140.002c5.52 0 10 4.476 10 10V57L80 87.998z"
                    fill="#62a598"
                  />
                </g>
              </g>
            </svg>
            <span>計算する</span>
          </button>
        </div>
        {children}
      </form>
    </React.Fragment>
  )
})
